import React from 'react';
import styled from 'styled-components';
import { useDroppable } from '@dnd-kit/core';
import { DndImageComponent } from './ImageComponent';
import { DndTextComponent } from './TextDndComponent';
import TrashIcon from '../assets/Trash.svg';

type SeqItemLite = {
  id: string | number;
  title?: string | null;
  items?: SeqItemLite[];
  pictures?:
    | {
        id: string | number;
        mediaUrl?: string;
        screenshotUrl?: string;
        name?: string;
      }[]
    | null;
};

export type ImageNode = { id: string; type: 'IMAGE'; subItem?: SeqItemLite };
export type TextNode = { id: string; type: 'TEXT'; subItem?: SeqItemLite };
export type ContentNode = ImageNode | TextNode;
export type ColumnNode = {
  id: string;
  type: 'COLUMN';
  children?: [ColumnNode, ColumnNode];
  content?: ContentNode[]; // IMAGE or TEXT nodes
};

type DndColumnProps = {
  node: ColumnNode;
  onDeleteColumn: (id: string) => void;
  onDeleteImage: (id: string) => void;
  readOnly?: boolean;
  sequenceItems?: SeqItemLite[] | undefined; // used to resolve subItem objects
};

const ColumnShell = styled.div<{ $readOnly?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  min-width: 0; /* allow children to shrink inside flex container */
  overflow: hidden; /* guard against children expanding */
  background: ${({ $readOnly }) =>
    $readOnly ? 'transparent' : '#f8f1ff'}; /* Match root canvas background */
`;

const SplitRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

const ContentArea = styled.div<{ $readOnly?: boolean }>`
  width: 100%;
  height: 100%;
  border: ${({ $readOnly }) =>
    $readOnly
      ? '2px dashed transparent'
      : '2px dashed #9738ff'}; /* Dotted outline color */
  border-radius: 8px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

const ImageItem = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

const ImageDeleteBtn = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  width: 30px;
  height: 30px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  z-index: 2;
  line-height: 0;

  &:hover {
    background: #f6f6f6;
  }
`;

const DroppableOverlay = styled.div<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  border: 2px dashed ${({ $active }) => ($active ? '#9738FF' : 'transparent')};
  border-radius: 8px;
  pointer-events: none;
`;

export function DndColumnComponent({
  node,
  onDeleteColumn,
  onDeleteImage,
  readOnly,
  sequenceItems
}: DndColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `column:${node.id}`,
    disabled: Boolean(node.children) || Boolean(readOnly)
  });

  const findRightmostLeafNode = (n: ColumnNode): ColumnNode => {
    if (!n.children) return n;
    const right = n.children[1];
    return findRightmostLeafNode(right);
  };

  const hasAnyContent = (n: ColumnNode): boolean => {
    if (n.content && n.content.length > 0) return true;
    if (n.children) {
      return hasAnyContent(n.children[0]) || hasAnyContent(n.children[1]);
    }
    return false;
  };

  const handleDelete = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    // If this column has content, delete last image
    if (node.content && node.content.length) {
      const last = node.content[node.content.length - 1];
      onDeleteImage(last.id);
      return;
    }
    // If this column has children, prefer deleting image from rightmost leaf; only prune if empty
    if (node.children) {
      const rightLeaf = findRightmostLeafNode(node);
      if (rightLeaf.content && rightLeaf.content.length) {
        const last = rightLeaf.content[rightLeaf.content.length - 1];
        onDeleteImage(last.id);
        return;
      }
      onDeleteColumn(rightLeaf.id);
      return;
    }
    // Leaf without content: prune this column
    onDeleteColumn(node.id);
  };
  return (
    <ColumnShell
      ref={setNodeRef}
      data-testid={`dndkit:column:${node.id}`}
      $readOnly={!!readOnly}
    >
      {node.children ? (
        <SplitRow>
          <DndColumnComponent
            node={node.children[0]}
            onDeleteColumn={onDeleteColumn}
            onDeleteImage={onDeleteImage}
            readOnly={readOnly}
            sequenceItems={sequenceItems}
          />
          <DndColumnComponent
            node={node.children[1]}
            onDeleteColumn={onDeleteColumn}
            onDeleteImage={onDeleteImage}
            readOnly={readOnly}
            sequenceItems={sequenceItems}
          />
        </SplitRow>
      ) : (
        <ContentArea $readOnly={!!readOnly}>
          {node.content?.map(item => {
            // Resolve the full subItem object to pass directly
            const findById = (
              items: SeqItemLite[] | undefined,
              id: string
            ): SeqItemLite | undefined => {
              if (!items) return undefined;
              for (const it of items) {
                if (String(it?.id) === id) return it;
                const found = findById(it.items, id);
                if (found) return found;
              }
              return undefined;
            };
            const subItem = item.subItem ?? findById(sequenceItems, item.id);
            return (
              <ContentDropBlocker
                key={item.id}
                id={item.id}
                readOnly={readOnly}
              >
                {item.type === 'IMAGE' ? (
                  <DndImageComponent subItem={subItem} subItemId={item.id} />
                ) : (
                  <DndTextComponent subItem={subItem} subItemId={item.id} />
                )}
                {!readOnly && (
                  <ImageDeleteBtn
                    type="button"
                    aria-label="Delete content"
                    onClick={e => {
                      e.stopPropagation();
                      onDeleteImage(item.id);
                    }}
                  >
                    <TrashIcon width={16} height={16} aria-hidden="true" />
                  </ImageDeleteBtn>
                )}
              </ContentDropBlocker>
            );
          })}
        </ContentArea>
      )}
      <DroppableOverlay $active={!readOnly && isOver && !node.children} />
      {!readOnly && !hasAnyContent(node) && (
        <DeleteButton
          type="button"
          aria-label="Delete column or content"
          onClick={handleDelete}
        >
          <TrashIcon width={16} height={16} aria-hidden="true" />
        </DeleteButton>
      )}
    </ColumnShell>
  );
}

// A wrapper that registers each content block as a droppable target to block drops on top of content.
function ContentDropBlocker({
  id,
  readOnly,
  children
}: {
  id: string;
  readOnly?: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef } = useDroppable({
    id: `content:${id}`,
    disabled: Boolean(readOnly)
  });
  return <ImageItem ref={setNodeRef}>{children}</ImageItem>;
}

// Keep a lightweight preview for the palette
const ColumnPreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px;
`;

const ColumnPreviewShape = styled.div`
  width: 28px;
  height: 40px;
  border: 1px dashed #9738ff;
  background: #f8f1ff;
  border-radius: 4px;
`;

export const createColumnComponentPreview = () => {
  return (
    <ColumnPreviewContainer>
      <ColumnPreviewShape />
      <div style={{ fontSize: '12px', color: '#666' }}>Column</div>
    </ColumnPreviewContainer>
  );
};

const DeleteButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  width: 30px;
  height: 30px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  z-index: 2;

  &:hover {
    background: #f6f6f6;
  }
`;
